import { useEffect, useState } from "react";
import { Row, Card, CardBody } from "reactstrap";
import { motion } from "framer-motion";
import ReactApexChart from "react-apexcharts";

import { getMcapWellbeingSummary } from "api/api.service";
import { getCached, setCached, mcapCacheKey } from "utils/mcapCache";
import KpiTile from "../components/KpiTile";
import typography from "../typography";

const GRAPH_COLORS = { teal: "#06402b", palette: ["#06402b", "#2b8a6b", "#58af54", "#84c98c", "#a9d8a3", "#c9e7c1", "#e3f3de", "#f0e6a3", "#e0b463", "#c98c58", "#b5651d"] };
const panelVariants = { hidden: { opacity: 0, scale: 0.98 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: [0.23, 1, 0.32, 1] } } };

export default function Wellbeing({ filters, filterBar }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = mcapCacheKey("wellbeing", filters);
    const cached = getCached(cacheKey);
    if (cached) {
      setData(cached);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    getMcapWellbeingSummary(filters)
      .then((res) => {
        if (cancelled) return;
        setData(res || {});
        setCached(cacheKey, res);
      })
      .catch(() => {
        if (!cancelled) setData({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  const moodTypes = data.moodTypeBreakdown || [];
  const streaks = data.streakDistribution || [];

  return (
    <div className="p-4 mt-3">
      {filterBar}
      <Row>
        <KpiTile index={0} label={typography.MOOD_COUNT} value={data.moodCount} loading={loading} colorClass="primary-gradient-bg" />
        <KpiTile index={1} label={typography.ACTIVE_MOOD_USERS} value={data.activeMoodUsers} loading={loading} colorClass="bg-orange" />
        <KpiTile index={2} label={typography.ANXIETY_COMPLETION} value={data.anxietyTestCompletionRate} suffix="%" loading={loading} colorClass="bg-blue" />
        <KpiTile index={3} label={typography.DEPRESSION_COMPLETION} value={data.depressionTestCompletionRate} suffix="%" loading={loading} colorClass="bg-purple" />
      </Row>

      <Row>
        <div className="col-xl-6 col-12">
          <motion.div initial="hidden" animate="visible" variants={panelVariants}>
            <Card className="mt-3">
              <CardBody>
                <h5 className="mb-3 font-family-lexend standard-typo-14-500-ld">{typography.MOOD_TYPE_BREAKDOWN}</h5>
                <ReactApexChart
                  type="donut"
                  height={280}
                  series={moodTypes.map((m) => m.count)}
                  options={{
                    labels: moodTypes.map((m) => m.moodType),
                    colors: GRAPH_COLORS.palette,
                    legend: { position: "bottom", fontSize: "12px", fontFamily: "Poppins" },
                    dataLabels: { enabled: false },
                  }}
                />
              </CardBody>
            </Card>
          </motion.div>
        </div>
        <div className="col-xl-6 col-12">
          <motion.div initial="hidden" animate="visible" variants={panelVariants}>
            <Card className="mt-3">
              <CardBody>
                <h5 className="mb-3 font-family-lexend standard-typo-14-500-ld">{typography.STREAK_DISTRIBUTION}</h5>
                <ReactApexChart
                  type="bar"
                  height={280}
                  series={[{ name: "Users", data: streaks.map((s) => s.userCount) }]}
                  options={{
                    chart: { toolbar: { show: false } },
                    plotOptions: { bar: { borderRadius: 4, columnWidth: "40%" } },
                    colors: [GRAPH_COLORS.teal],
                    dataLabels: { enabled: false },
                    xaxis: {
                      categories: streaks.map((s) => `${s.bucket} days`),
                      labels: { style: { fontSize: "12px", fontFamily: "Poppins" } },
                      axisBorder: { show: false },
                      axisTicks: { show: false },
                    },
                    grid: { borderColor: "#f6f6f6" },
                  }}
                />
              </CardBody>
            </Card>
          </motion.div>
        </div>
      </Row>
    </div>
  );
}
